import React, { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import dayjs from "dayjs";
import { CalendarEvent } from "../../hooks/useCalendarEvents";

export interface MeetingPreviewDialogProps {
  open: boolean;
  event: CalendarEvent | null;
  onClose: () => void;
  /** Open the full meeting record. Only offered for Prospo meetings. */
  onEdit?: (meeting: any) => void;
  /** Promote a calendar event that has no Prospo record yet. */
  onAddToProspo?: (event: CalendarEvent) => void;
}

/** One labelled block with a leading icon. Renders nothing when it has no value. */
const Field: React.FC<{
  icon: React.ReactNode;
  label: string;
  children?: React.ReactNode;
}> = ({ icon, label, children }) => {
  if (!children) return null;
  return (
    <Box sx={{ display: "flex", gap: 1.25, mb: 2 }}>
      <Box sx={{ color: "#9ca3af", display: "flex", pt: "2px" }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "#6b7280",
            mb: 0.25,
          }}
        >
          {label}
        </Typography>
        {children}
      </Box>
    </Box>
  );
};

/** "Ada Lovelace" from a Graph emailAddress node, falling back to the address. */
const graphPersonName = (node: any): string | undefined =>
  node?.emailAddress?.name || node?.emailAddress?.address;

/**
 * Names for the people on a meeting, gathered from every place they can live.
 *
 * A Prospo meeting created by an advisor carries relational `clients`/`advisors`.
 * A *synced* meeting (Outlook/Teams/Zoom) is exempt from the "at least one
 * client" rule and often has both arrays empty — its real attendee list is the
 * Graph payload parked on `MeetingMediumMetaData.event`. Reading only the
 * relational fields showed "no attendees" on exactly those meetings (#2083).
 */
const attendeeNames = (event: CalendarEvent): string[] => {
  const people: (string | undefined)[] = [];

  if (event.inProspo) {
    const m = event.meeting || {};
    people.push(
      ...(m.clients || []).map((c: any) => c?.name || c?.email),
      ...(m.advisors || []).map((a: any) =>
        [a?.firstName, a?.lastName].filter(Boolean).join(" ") || a?.email,
      ),
      // Plain email strings on older/synced records.
      ...(m.participants || []),
    );

    // Synced meetings keep the original invite here, which is the only place the
    // full attendee list survives for a meeting with no linked contacts.
    const graphEvent = m.MeetingMediumMetaData?.event;
    if (graphEvent) {
      people.push(graphPersonName(graphEvent.organizer));
      people.push(...(graphEvent.attendees || []).map(graphPersonName));
    }
  } else {
    people.push(...((event.raw?.attendees || []) as any[]).map((a) => a?.name || a?.email));
  }

  // Dedupe case-insensitively — the same person often appears both as a linked
  // contact and as a Graph attendee, spelled differently.
  const seen = new Map<string, string>();
  people
    .filter(Boolean)
    .map((p) => String(p).trim())
    .filter((p) => p.length > 0)
    .forEach((p) => {
      const key = p.toLowerCase();
      if (!seen.has(key)) seen.set(key, p);
    });
  return Array.from(seen.values());
};

/**
 * Graph stores an event body as HTML. This is a preview, not a renderer, so
 * reduce it to readable text rather than injecting markup.
 */
const stripHtml = (html: string): string =>
  html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    // Teams invites pad the body with long divider runs and blank lines.
    .replace(/[_\-=]{10,}/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/**
 * Same fallback chain as the attendees: a synced meeting's body lives on the
 * stored Graph event, not in Prospo's own `description` column.
 */
const descriptionOf = (event: CalendarEvent): string => {
  if (!event.inProspo) return String(event.raw?.bodyPreview || "").trim();
  const m = event.meeting || {};
  const graphEvent = m.MeetingMediumMetaData?.event;
  // bodyPreview is already plain text; body.content is HTML and needs stripping.
  const plain = m.description || graphEvent?.bodyPreview;
  if (plain && String(plain).trim()) return String(plain).trim();
  const html = graphEvent?.body?.content;
  return html ? stripHtml(String(html)) : "";
};

/**
 * Read-only preview of a calendar entry (#2082).
 *
 * Clicking an event used to jump straight to the meeting page, which is a heavy
 * move for "what is this?" — and it did nothing at all for events not yet in
 * Prospo. This shows the detail in place, and offers Edit only where there's a
 * Prospo record to edit; unimported events get "Add to Prospo" instead.
 */
const MeetingPreviewDialog: React.FC<MeetingPreviewDialogProps> = ({
  open,
  event,
  onClose,
  onEdit,
  onAddToProspo,
}) => {
  const when = useMemo(() => {
    if (!event) return "";
    const start = dayjs(event.start);
    const end = dayjs(event.end);
    const sameDay = start.isSame(end, "day");
    return sameDay
      ? `${start.format("ddd D MMM YYYY")} · ${start.format("h:mm A")} – ${end.format("h:mm A")}`
      : `${start.format("ddd D MMM YYYY h:mm A")} – ${end.format("ddd D MMM YYYY h:mm A")}`;
  }, [event]);

  const people = useMemo(() => (event ? attendeeNames(event) : []), [event]);
  const description = useMemo(() => (event ? descriptionOf(event) : ""), [event]);

  if (!event) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, pb: 0.5, pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>{event.title}</Box>
          <Chip
            label={event.inProspo ? "In Prospo" : "Not in Prospo"}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 600,
              bgcolor: event.inProspo ? "#eff6ff" : "#f3f4f6",
              color: event.inProspo ? "#1d4ed8" : "#4b5563",
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Divider sx={{ mb: 2 }} />

        <Field icon={<EventOutlinedIcon fontSize="small" />} label="When">
          <Typography sx={{ fontSize: "0.9rem", color: "#111827" }}>{when}</Typography>
        </Field>

        <Field icon={<VideocamOutlinedIcon fontSize="small" />} label="Where">
          {event.meetingMedium ? (
            <Box>
              <Typography sx={{ fontSize: "0.9rem", color: "#111827" }}>
                {event.meetingMedium}
              </Typography>
              {event.joinUrl && (
                <Typography
                  component="a"
                  href={event.joinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: "0.85rem", color: "#2563eb", wordBreak: "break-all" }}
                >
                  Join call
                </Typography>
              )}
            </Box>
          ) : null}
        </Field>

        <Field icon={<PeopleOutlineIcon fontSize="small" />} label="Attendees">
          {people.length ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {people.map((name) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  sx={{ height: 24, fontSize: "0.78rem", bgcolor: "#f9fafb" }}
                />
              ))}
            </Box>
          ) : null}
        </Field>

        <Field icon={<NotesOutlinedIcon fontSize="small" />} label="Description">
          {description ? (
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: "#374151",
                whiteSpace: "pre-wrap",
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {description}
            </Typography>
          ) : null}
        </Field>

        {!people.length && !description && (
          <Typography sx={{ fontSize: "0.85rem", color: "#9ca3af" }}>
            No description or attendees recorded for this meeting.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
        {/* Edit only exists where there's a Prospo record behind the event.
            An Outlook-only event has nothing to edit yet — it gets Add instead. */}
        {event.inProspo ? (
          <Button
            variant="contained"
            onClick={() => onEdit?.(event.meeting)}
            sx={{ textTransform: "none" }}
          >
            Edit meeting
          </Button>
        ) : (
          onAddToProspo && (
            <Button
              variant="contained"
              onClick={() => onAddToProspo(event)}
              sx={{ textTransform: "none" }}
            >
              Add to Prospo
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MeetingPreviewDialog;
